/** @jsxImportSource @emotion/react */
import { BaseDialog } from "@erinfo/brand-ui/src/storybook/other/dialogs/BaseDialog"
import { CammeraPermissionDialog } from "@erinfo/brand-ui/src/storybook/other/dialogs/CameraPermissionDialog"
import * as env from "@erinfo/env"
import { isMobileDevice } from "@erinfo/react-utils/src/helpers/check-browser"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos"
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary"
import { Box, IconButton, Typography } from "@mui/material"
import { CameraType, CameraView } from "expo-camera"
import Camera from "expo-camera/src/ExpoCameraManager.web"
import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"

import { FileDropzone } from "../FileDropzone2"

const standardDev = (arr) => {
  const mean =
    arr.reduce((acc, curr) => {
      return acc + curr
    }, 0) / arr.length
  arr = arr.map((k) => {
    return (k - mean) ** 2
  })
  const sum = arr.reduce((acc, curr) => acc + curr, 0)
  return Math.sqrt(sum / arr.length)
}

const checkForImageIssue = async (cameraRef) => {
  try {
    const canvas = document.createElement(`canvas`)
    const ctx = canvas.getContext(`2d`)
    if (!ctx) return // Guard against null context

    const image = new Image()
    image.onload = () => {
      const imageSize = 100
      ctx.drawImage(image, 0, 0)
      const imageData = ctx.getImageData(0, 0, imageSize, imageSize)
      const maxColorValue = 255
      const imgArr = imageData?.data.filter((i) => i !== maxColorValue)
      const std = standardDev(imgArr || [])
      const minStandardDeviation = 5
      if (std < minStandardDeviation) {
        void env.newrelic.browser.helpers.withNewRelic((nr) => {
          nr.noticeError(
            new Error(`Camera image issue, standard deviation: ${std}`),
          )
        })
      }
    }

    const delay = 300
    const maxRetries = 3
    const takePictureWithRetry = async (retryCount = 0) => {
      const delayMs = delay * (retryCount + 1) // Increase delay with each retry

      try {
        const pic = await cameraRef.current?.takePictureAsync()
        if (!pic?.uri) {
          console.log(`no uri`)
          if (retryCount < maxRetries) {
            console.log(`Retrying takePictureAsync, attempt ${retryCount + 2}`)
            setTimeout(
              async () => await takePictureWithRetry(retryCount + 1),
              delayMs,
            )
          }
          return
        }
        image.src = pic?.uri
      } catch (picError) {
        console.log(`takePictureAsync failed:`, picError)
        if (retryCount < maxRetries) {
          console.log(
            `Retrying takePictureAsync due to error, attempt ${retryCount + 2}`,
          )
          setTimeout(
            async () => await takePictureWithRetry(retryCount + 1),
            delayMs,
          )
        } else {
          console.log(`Max retries reached for takePictureAsync`)
          await env.newrelic.browser.helpers.withNewRelic((nr) => {
            nr.noticeError(
              new Error(`Camera error: ${JSON.stringify(picError, null, 2)}`),
            )
          })
        }
      }
    }

    setTimeout(takePictureWithRetry, delay)
  } catch (err) {
    await env.newrelic.browser.helpers.withNewRelic((nr) => {
      nr.noticeError(new Error(`Camera error: ${JSON.stringify(err, null, 2)}`))
    })
    console.log(err)
  }
}

interface IProps {
  isOpen: boolean
  onClose: () => void
  onCapture: (base64Img?: string) => void
  startingCameraType?: CameraType
}

export const CameraCaptureDialog = ({
  isOpen,
  onClose,
  onCapture,
  startingCameraType = `back`,
}: IProps) => {
  const cameraRef = useRef<CameraView>(null)
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null)
  const [cameraTypes, setCameraTypes] = useState<CameraType[]>()
  const [cameraType, setCameraType] = useState<CameraType>(startingCameraType)
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    // getUserMedia for better browser compatibility (Chrome 47+, Firefox 36+, Safari 11+)
    // This approach works by testing actual camera access rather than checking permissions
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        // Permission granted - stop the stream immediately as we just needed to test
        stream.getTracks().forEach((track) => track.stop())
        setCameraPermission(true)
      })
      .catch((error) => {
        // Permission denied or camera not available
        console.log(`Camera access error:`, error.name, error.message)
        if (error.name === `NotAllowedError`) {
          setCameraPermission(false)
        } else if (error.name === `NotFoundError`) {
          setCameraPermission(false)
        } else {
          // For other errors, show the settings modal to guide user
          setShowSettingsModal(true)
        }
      })
  }, [isOpen])

  const getTypes = async () => {
    if (cameraTypes?.length) return
    const types = (await Camera.getAvailableCameraTypesAsync()) as CameraType[]
    setCameraTypes(types)
    setCameraType(types.includes(`back`) ? `back` : `front`)
  }

  return (
    <BaseDialog
      showCloseButton
      open={isOpen}
      handleClose={onClose}
      Icon={<CameraAltIcon sx={{ opacity: 0.54, mr: 2 }} />}
      title="Capture Image"
      renderActions={() => (
        <Box
          sx={{
            width: `100%`,
            display: `flex`,
            justifyContent: `space-between`,
            alignItems: `center`,
          }}
        >
          <IconButton>
            <FileDropzone
              setBase64={onCapture}
              sx={{
                cursor: `pointer`,
                padding: `3px 5px`,
                border: `none`,
              }}
            >
              <PhotoLibraryIcon color="primary" />
            </FileDropzone>
          </IconButton>
          {(cameraTypes?.length ?? 0) > 1 && (
            <IconButton
              onClick={() =>
                setCameraType((p) => (p === `back` ? `front` : `back`))
              }
            >
              <FlipCameraIosIcon />
            </IconButton>
          )}
        </Box>
      )}
      actionsSx={{ padding: 0 }}
      contentSx={(theme) => ({
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        "& >div:first-of-type": {
          [theme.breakpoints.down(`sm`)]: {
            height: `50vh`,
            width: `90vw`,
            minWidth: `100%`,
          },
          [theme.breakpoints.up(`sm`)]: {
            width: `400px`,
            height: `500px `,
            minWidth: `100%`,
          },
        },
      })}
    >
      {showSettingsModal && isMobileDevice && (
        <CammeraPermissionDialog
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
      {cameraPermission === false ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          sx={{ width: `100%` }}
        >
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            sx={{ maxWidth: `300px`, textAlign: `start` }}
          >
            <Typography variant="h5">We can't find your camera</Typography>
            <Typography>
              Access Settings to make sure that it is enabled
            </Typography>
            <FileDropzone
              setBase64={onCapture}
              sx={{
                marginTop: `20px`,
                padding: 0,
                cursor: `pointer`,
                border: `none`,
              }}
            >
              <Link to="#">Photo Library</Link>
            </FileDropzone>
          </Box>
        </Box>
      ) : (
        <>
          <CameraView
            ref={cameraRef}
            onCameraReady={async () => {
              await getTypes()
              void checkForImageIssue(cameraRef)
            }}
            mute // we don't need audio, as we take a picture for identification
            mode={`video`}
            facing={cameraType}
            key={cameraType}
            onMountError={async (e) => {
              await env.newrelic.browser.helpers.withNewRelic((nr) => {
                nr.noticeError(
                  new Error(`Camera error: ${JSON.stringify(e, null, 2)}`),
                )
              })
              console.log(e)
            }}
          />
          <Box
            onClick={async () => {
              const result = await cameraRef.current?.takePictureAsync()
              onCapture(result?.base64)
            }}
            sx={(theme) => ({
              [theme.breakpoints.down(`sm`)]: {
                width: `70px`,
                height: `70px`,
                marginTop: `-35px`,
                marginLeft: `-35px`,
                bottom: `12%`,
              },
              [theme.breakpoints.up(`sm`)]: {
                width: `100px`,
                height: `100px`,
                marginTop: `-50px`,
                marginLeft: `-50px`,
                bottom: `10%`,
              },
              left: `50%`,
              position: `absolute`,
              transition: `all 0.25s`,
              cursor: `pointer`,
              "&:hover": {
                "& .circle": {
                  opacity: 1,
                },
              },
              "&:active": {
                "& .ring": {
                  opactity: 1,
                },
                "& .circle": {
                  opacity: 0.5,
                },
              },
            })}
          >
            <Box
              className="circle"
              sx={{
                position: `absolute`,
                top: `12%`,
                left: `12%`,
                bottom: `12%`,
                right: `12%`,
                borderRadius: `100%`,
                backgroundColor: `#ffffff`,
                opacity: 0,
                transition: `all 0.25s`,
              }}
            />
            <Box
              className="ring"
              sx={{
                position: `absolute`,
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,

                borderRadius: `100%`,
                border: `0.5em solid #ffffff`,
                opacity: `0.8`,
                transition: `all 0.25s`,
              }}
            />
          </Box>
        </>
      )}
    </BaseDialog>
  )
}
